import React, { useState, useEffect } from 'react';
import GenericModal from '../GenericModal';
import PreviewPDF from '../Widgets/PreviewPDF';
import NotesModal from './NotesModal';
import { ClientMessageModel, ClientModel, AddressModel } from '../../Utils/Models';
import { Vocabulary } from '../../Utils/Vocabulary';
import { postData } from '../../Services/postData';
import { updateData } from '../../Services/updateData';
import { endpoints } from '../../Utils/UrlEnum';
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  Card,
  CardMedia,
  CardContent,
  Paper,
  Stack,
  Divider,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Close,
  Description,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday,
  Person,
  NoteAdd
} from '@mui/icons-material';
import { EWidths } from '../../Utils/Enums';
import { useTheme } from '@mui/material/styles';
import { StatusItem } from '../../Utils/Types';

interface MessageModalProps {
  open: boolean;
  onClose: () => void;
  message: ClientMessageModel | null;
  onSave: () => void;
  statuses?: StatusItem[] | null;
}

export default function MessageModal({ open, onClose, message, onSave, statuses }: MessageModalProps) {
  const theme = useTheme();
  const [status, setStatus] = useState(message?.status || 'pending');
  const [previewFile, setPreviewFile] = useState<{ url: string; type: string; name: string; } | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);

  // Update status when message changes
  useEffect(() => {
    setStatus(message?.status || 'pending');
  }, [message]);

  if (!message) return null;

  const handleSave = async () => {
    try {
      // Update the message status via API

      if (message._id) {
        await updateData(`${endpoints.messages.main}/${message._id}`, { status });

        // Call the onSave callback if provided
        await onSave();

        onClose();
      }
    } catch (error) {
      console.error(`${Vocabulary.saveError}:`, error);
      // You can add an error notification here
    }
  };

  const handleFilePreviewClick = (file: string, e: React.MouseEvent) => {
    e.preventDefault();
    const ext = file.split('.').pop()?.toLowerCase();
    const fileUrl = `${endpoints.media.files}${file}`;

    if (ext === 'pdf') {
      setPreviewFile({ url: fileUrl, type: 'pdf', name: file.split('/').pop() || file });
      setShowPdfPreview(true);
    } else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext || '')) {
      setPreviewFile({ url: fileUrl, type: 'image', name: file.split('/').pop() || file });
    }
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
    setShowPdfPreview(false);
  };

  const messageText = message.message && message.message.length > 0 ? message.message[0] : '';

  const handleCreateClient = async () => {
    try {
      // Parse the message text to extract client data
      const parsedData = parseMessageToClientData(messageText);

      // Create a new client object
      const newClient = new ClientModel();
      newClient.name = message.name || '';
      newClient.email = message.email || '';
      newClient.phone = message.phone ? [message.phone] : [''];

      // Set fiscal data based on parsed information
      if (parsedData.cui) {
        newClient.fiscalCode = parsedData.cui;
      }

      // Set legal person status
      newClient.isLegalPerson = parsedData.isLegalPerson;

      // Set address with equipment location
      if (parsedData.equipmentAddress) {
        const address = new AddressModel();
        address.details = parsedData.equipmentAddress;
        newClient.address = address;
      }

      // Add observations with equipment details
      const observations = [];
      if (parsedData.defectEquipment) {
        observations.push(`${Vocabulary.brokenDevice}: ${parsedData.defectEquipment}`);
      }
      if (parsedData.equipmentSerial) {
        observations.push(`${Vocabulary.deviceCode}: ${parsedData.equipmentSerial}`);
      }
      if (parsedData.reportedDefect) {
        observations.push(`${Vocabulary.deviceBrokenUnit}: ${parsedData.reportedDefect}`);
      }
      newClient.observations = observations.join('\n');

      // Send to API to create client
      const response = await postData(endpoints.client.main, newClient);

      if (response && response.data) {
        const createdClientId = response.data._id;

        // Update the message with the clientId
        if (message._id) {
          await updateData(`${endpoints.messages.main}/${message._id}`, { clientId: createdClientId });

          // Update local message state

          await onSave();

        }

        onClose();
      }
    } catch (error) {
      console.error(`${Vocabulary.saveError}:`, error);
      // You can add an error notification here
    }
  };

  // Helper function to parse message text
  const parseMessageToClientData = (text: string) => {
    const data: {
      equipmentAddress: string;
      isLegalPerson: boolean;
      cui: string;
      defectEquipment: string;
      equipmentSerial: string;
      reportedDefect: string;
    } = {
      equipmentAddress: '',
      isLegalPerson: false,
      cui: '',
      defectEquipment: '',
      equipmentSerial: '',
      reportedDefect: ''
    };

    // Split by new lines
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);

    lines.forEach(line => {
      if (line.includes('Adresa unde se află echipamentul:')) {
        data.equipmentAddress = line.split('Adresa unde se află echipamentul:')[1]?.trim() || '';
      } else if (line.includes('Persoană fizică')) {
        data.isLegalPerson = false;
      } else if (line.includes('Persoană juridică')) {
        data.isLegalPerson = true;
      } else if (line.includes('CUI:')) {
        data.cui = line.split('CUI:')[1]?.trim() || '';
      } else if (line.includes('Echipamentul defect:')) {
        data.defectEquipment = line.split('Echipamentul defect:')[1]?.trim() || '';
      } else if (line.includes('Serie echipament:')) {
        data.equipmentSerial = line.split('Serie echipament:')[1]?.trim() || '';
      } else if (line.includes('Defecțiune sesizată:')) {
        data.reportedDefect = line.split('Defecțiune sesizată:')[1]?.trim() || '';
      }
    });

    return data;
  };

  const imageFiles = message.files?.filter(file => {
    const ext = file.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext || '');
  }) || [];

  const otherFiles = message.files?.filter(file => {
    const ext = file.split('.').pop()?.toLowerCase();
    return !['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext || '');
  }) || [];

  return (
    <>
      {/* Notes Modal - Positioned to the left */}
      <NotesModal
        open={showNotesModal && open}
        onClose={() => setShowNotesModal(false)}
        messageId={message?._id}
        messageName={message?.name || 'Client Message'}
      />

      <GenericModal
        open={open}
        onClose={onClose}
        title={Vocabulary.message}
        maxWidth={EWidths.MD}
      >
        {/* Notes Button - Positioned at top right of modal content */}
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 56,
            zIndex: 1
          }}
        >
          <Tooltip title={Vocabulary.notes} placement="left">
            <IconButton
              onClick={() => setShowNotesModal(!showNotesModal)}
              sx={{
                color: "#fff",
                backgroundColor: showNotesModal ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: showNotesModal ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <NoteAdd />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Main Content */}
        <Box sx={{ p: 3, maxHeight: '70vh', overflow: 'auto' }}>
          {/* Contact Information Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : 'rgba(255,255,255,0.05)'
            }}
          >
            <Stack spacing={2.5}>
              {/* Name Field */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Person sx={{ color: theme.palette.primary.light, fontSize: 20 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                    {Vocabulary.name}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {message.name || '-'}
                  </Typography>
                </Box>
              </Box>

              {/* Email Field */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <EmailIcon sx={{ color: theme.palette.primary.light, fontSize: 20 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                    {Vocabulary.email}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {message.email || '-'}
                  </Typography>
                </Box>
              </Box>

              {/* Phone Field */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PhoneIcon sx={{ color: theme.palette.primary.light, fontSize: 20 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                    {Vocabulary.phone}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {message.phone || '-'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Message History */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600 }}>
                  {Vocabulary.messageHistory}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderLeft: `4px solid ${theme.palette.primary.light}`,
                    backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : 'rgba(0,0,0,0.2)',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {message?.createdAt ? new Date(message.createdAt).toLocaleDateString('ro-RO', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      }) : ''}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.7,
                      color: 'text.primary'
                    }}
                  >
                    {messageText || 'N/A'}
                  </Typography>
                </Paper>
              </Box>

              {/* Create Client Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 1 }}>
                {message.clientId && (
                  <Chip
                    label={Vocabulary.clientAlreadyCreated}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateClient}
                  disabled={!!message.clientId}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4
                    },
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled
                    }
                  }}
                >
                  {Vocabulary.createClient}
                </Button>
              </Box>
            </Stack>
          </Paper>

          {/* Status Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : 'rgba(255,255,255,0.05)'
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600 }}>
              {Vocabulary.status}
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap style={{ display: 'flex', justifyContent: "center" }}>
              {status != null && statuses?.map((statusItem) => (
                <Chip
                  key={statusItem._id}
                  label={Vocabulary[statusItem.label.toLowerCase() as keyof typeof Vocabulary] || statusItem.label}
                  onClick={() => setStatus(statusItem.name)}
                  sx={{
                    backgroundColor: status === statusItem.name ? statusItem.color : theme.palette.mode === 'light' ? '#fff' : 'rgba(255,255,255,0.08)',
                    color: status === statusItem.name ? '#fff' : 'text.primary',
                    border: status === statusItem.name ? 'none' : `1px solid ${theme.palette.divider}`,
                    fontWeight: status === statusItem.name ? 600 : 500,
                    px: 2,
                    py: 2.5,
                    fontSize: '0.75rem',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: status === statusItem.name ? statusItem.color : theme.palette.action.hover,
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    }
                  }}
                />
              ))}
            </Stack>
          </Paper>

          {/* Files Section */}
          {message.files && message.files.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : 'rgba(255,255,255,0.05)'
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'text.secondary', fontWeight: 600 }}>
                {Vocabulary.attacheFiles}
              </Typography>

              {/* Image Previews */}
              {imageFiles.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {imageFiles.map((file) => (
                    <Grid item xs={6} sm={4} md={3} key={file}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          borderRadius: 2,
                          border: `2px solid ${theme.palette.divider}`,
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 6,
                            borderColor: theme.palette.primary.light
                          }
                        }}
                        onClick={(e) => handleFilePreviewClick(file, e)}
                      >
                        <CardMedia
                          component="img"
                          height="140"
                          image={`${endpoints.media.files}${file}`}
                          alt={file.split('/').pop()}
                          sx={{
                            objectFit: 'cover',
                            backgroundColor: '#f5f5f5'
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                        <CardContent sx={{ p: 1.5, backgroundColor: theme.palette.background.paper }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              textAlign: 'center',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'text.secondary'
                            }}
                          >
                            {file.split('/').pop()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Other Files List */}
              {otherFiles.length > 0 && (
                <Box>
                  {imageFiles.length > 0 && (
                    <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 600 }}>
                      Alte fișiere:
                    </Typography>
                  )}
                  <Stack spacing={1.5}>
                    {otherFiles.map((file) => (
                      <Paper
                        key={file}
                        elevation={0}
                        sx={{
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          cursor: 'pointer',
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.paper,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                            borderColor: theme.palette.primary.light,
                            transform: 'translateX(4px)'
                          }
                        }}
                        onClick={(e) => handleFilePreviewClick(file, e)}
                      >
                        <Description sx={{ color: theme.palette.primary.light }} />
                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                          {file.split('/').pop()}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>
          )}

          {/* Save Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4
                }
              }}
            >
              {Vocabulary.save.toUpperCase()}
            </Button>
          </Box>
        </Box>

        {/* PDF Preview Modal */}
        {showPdfPreview && previewFile && previewFile.type === 'pdf' && (
          <PreviewPDF
            url={previewFile.url}
            htmlContent=""
            open={showPdfPreview}
            onClose={handleClosePreview}
            title={previewFile.name}
          />
        )}

        {/* Image Preview Modal */}
        {previewFile && previewFile.type === 'image' && (
          <Dialog
            open={true}
            onClose={handleClosePreview}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                overflow: 'hidden'
              }
            }}
          >
            <DialogContent sx={{ position: 'relative', p: 0, backgroundColor: '#000' }}>
              <IconButton
                onClick={handleClosePreview}
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: 10,
                  color: '#fff',
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  zIndex: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.8)'
                  }
                }}
              >
                <Close />
              </IconButton>
              <Box
                component="img"
                src={previewFile.url}
                alt={previewFile.name}
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
              <Box
                sx={{
                  p: 2,
                  textAlign: 'center',
                  color: '#fff',
                  backgroundColor: 'rgba(0,0,0,0.8)'
                }}
              >
                <Typography variant="body2">{previewFile.name}</Typography>
              </Box>
            </DialogContent>
          </Dialog>
        )}
      </GenericModal>
    </>
  );
}
